import { FunctionComponent, useState } from "react";
import { useValidationGroup } from "../../hooks/useValidation";
import { EvalAnswer, EvalAnswerType } from "../../models/evaluation";
import { InsertAnswerAsync, UpdateAnswerAsync } from "../../services/http-service";
import { RangeValidator, RequiredValidator } from "../Validator";
import { EvalEditorSidebar } from "./EvalEditorSidebar";
import notify from 'devextreme/ui/notify';
import { Spinner } from "../spinner/Spinner";
import { setFlagsFromString } from "v8";

export interface EvalEditorAnswerDetailsProps {
    answer?: EvalAnswerType,
    onAnswerTypeUpdated?: (answer: EvalAnswerType) => void,
    onAnswerUpdated?: (i: number, answer: EvalAnswer) => void,
}

export const EvalEditorAnswerDetails: FunctionComponent<EvalEditorAnswerDetailsProps> = ({ answer, onAnswerUpdated, onAnswerTypeUpdated }) => {
    const group = useValidationGroup();
    const [isLoading, setLoading] = useState(false);

    return (
        <EvalEditorSidebar title="Details" img="clipboard-fill" className="border-start" >
            <div className='bg-white eval-sidebar'>
                {answer !== undefined &&
                    <div>
                        <div className="bg-light border" style={{ width: '100%', position: 'sticky', top: '0', marginTop: 0 }}>
                            <span
                                onClick={() => {
                                    answer?.evaluationAnswers.push({ answer: 'new answer', multiplier: 0, isNeutral: false });
                                    if (answer) {
                                        onAnswerTypeUpdated?.(answer);
                                    }
                                }}
                                className="btn btn-dark btn-sm m-2">
                                <i className="bi bi-plus-square-fill fs-6" /> Add Answer
                            </span>
                            <span
                                onClick={async () => {
                                    if (answer && group.causeValidation()) {
                                        setLoading(true);

                                        try {
                                            if (answer.uid === undefined) {
                                                const d = await InsertAnswerAsync(answer.name);
                                                if (!d.ok) {
                                                    notify(`Error while create answer type: ${d.error?.message}`, 'error', 2500);
                                                } else {
                                                    const answers = answer.evaluationAnswers;
                                                    answer = d.data!;
                                                    answer.evaluationAnswers = answers;
                                                    onAnswerTypeUpdated?.(answer);

                                                }
                                            }

                                            const d = await UpdateAnswerAsync(answer);
                                            if (d.ok && d.data !== undefined) {
                                                onAnswerTypeUpdated?.(d.data);
                                                notify('The changes were saved successfully!', 'success', 1800);
                                            } else {
                                                notify(`There was an error while saving: ${d.error?.message}`, 'error', 2500);
                                            }
                                        }
                                        catch (e) {
                                            notify(`Internal server error: ${e}`, 'error', 2500);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }
                                }}
                                className="btn btn-dark btn-sm m-2">
                                <i className="bi bi-save-fill fs-6" /> Save Changes
                                {isLoading &&
                                    <Spinner size="sm" />
                                }
                            </span>
                        </div>
                        <div className="row mb-1 mx-1">
                            <div className="col">
                                <label className="form-label" htmlFor="questionInput">Name:</label>
                                <input
                                    type={'text'}
                                    id="questionInput"
                                    className='form-control rounded-0'
                                    onChange={(e) => { if (answer) { answer.name = e.target.value; onAnswerTypeUpdated?.(answer); } }}
                                    value={answer.name}
                                    readOnly={false}
                                    required />
                                <RequiredValidator group={group} value={answer?.name} />
                            </div>
                        </div>
                        <div className="row mb-1 mx-1">
                            {answer.evaluationAnswers.map((a, i) =>
                                <div className="border mb-1" key={i}>
                                    <span
                                        onClick={() => {
                                            if (answer) {
                                                answer.evaluationAnswers.splice(i, 1);
                                                onAnswerTypeUpdated?.(answer);
                                            }
                                        }}
                                        className="btn btn-danger btn-sm m-1">
                                        <i className="bi bi-trash-fill fs-6" /> Delete Question
                                    </span>
                                    <div className="mb-1 py-1">
                                        <label className="form-label" htmlFor="answerInput">Answer Text:</label>
                                        <input
                                            type={'text'}
                                            id="answerInput"
                                            className='form-control mb-1'
                                            onChange={(e) => { a.answer = e.target.value; onAnswerUpdated?.call(undefined, i, a); }}
                                            value={a.answer} />
                                        <RequiredValidator group={group} value={a.answer} />
                                        <div className="row align-items-center">
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="multiplierInput">Multiplier: </label>
                                                <input
                                                    type={'number'}
                                                    id="multiplierInput"
                                                    className='form-control'
                                                    onChange={(e) => {
                                                        if (!Number.isNaN(e.target.valueAsNumber))
                                                            a.multiplier = e.target.valueAsNumber;
                                                        onAnswerUpdated?.call(undefined, i, a);
                                                    }}
                                                    value={a.multiplier} />
                                                <RangeValidator min={0} group={group} value={a.multiplier} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="isNeutralInput">Neutral: </label>
                                                <input
                                                    type={'checkbox'}
                                                    id="isNeutralInput"
                                                    className='form-check-input'
                                                    onChange={(e) => { a.isNeutral = e.target.checked; onAnswerUpdated?.call(undefined, i, a); }}
                                                    checked={a.isNeutral} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }
            </div>
        </EvalEditorSidebar >
    );
};